import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface MonthYearPickerProps {
  onSelect: (month: number, year: number, day?: number) => void;
  initialMonth?: number;
  initialYear?: number;
  initialDay?: number;
  showDay?: boolean;
  label?: string;
}

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  onSelect,
  initialMonth = new Date().getMonth() + 1,
  initialYear = new Date().getFullYear(),
  initialDay,
  showDay = false,
  label = 'Select Date'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedDay, setSelectedDay] = useState<number | undefined>(initialDay);
  const [displayMonth, setDisplayMonth] = useState(initialMonth);
  const [displayYear, setDisplayYear] = useState(initialYear);
  const [displayDay, setDisplayDay] = useState<number | undefined>(initialDay);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (inputRef.current && inputRef.current.contains(target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  const handleOpen = () => {
    if (!isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
    setIsOpen(prev => !prev);
  };

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    setDisplayMonth(month);
  };

  const handleApply = () => {
    setSelectedYear(displayYear);
    onSelect(selectedMonth, displayYear, showDay ? displayDay : undefined);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setDisplayMonth(selectedMonth);
    setDisplayYear(selectedYear);
    setDisplayDay(selectedDay);
    setIsOpen(false);
  };

  const displayText = showDay && selectedDay
    ? `${months[selectedMonth - 1]} ${selectedDay}, ${selectedYear}`
    : `${months[selectedMonth - 1]} ${selectedYear}`;

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width - 600,
        backgroundColor: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        padding: '12px',
        zIndex: 99999,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {months[displayMonth - 1]} {displayYear}
        </span>
      </div>

      {/* Year navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '10px',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <button
          type="button"
          onClick={() => setDisplayYear(y => y - 1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', fontSize: '14px' }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span style={{ fontWeight: 'bold', fontSize: '14px', minWidth: '60px', textAlign: 'center' }}>
          {displayYear}
        </span>
        <button
          type="button"
          onClick={() => setDisplayYear(y => y + 1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', fontSize: '14px' }}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      {/* Month grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '12px' }}>
        {months.map((m, i) => (
          <button
            key={m}
            type="button"
            onClick={() => handleMonthSelect(i + 1)}
            style={{
              padding: '6px 4px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: selectedMonth === i + 1 ? '#000' : '#f0f0f0',
              color: selectedMonth === i + 1 ? '#fff' : '#333',
              fontWeight: selectedMonth === i + 1 ? 'bold' : 'normal',
              fontSize: '12px',
            }}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Day selector (optional) */}
      {showDay && (
        <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e0e0e0' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>
            Day (Optional)
          </label>
          <input
            type="number"
            min="1"
            max="31"
            placeholder="Leave empty to skip"
            value={displayDay || ''}
            onChange={(e) => {
              const value = e.target.value.trim();
              if (value === '') {
                setDisplayDay(undefined);
                setSelectedDay(undefined);
              } else {
                const day = Math.min(31, Math.max(1, parseInt(value) || 1));
                setDisplayDay(day);
                setSelectedDay(day);
              }
            }}
            style={{
              width: '100%',
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '13px',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleCancel}
          style={{
            padding: '6px 14px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          style={{
            padding: '6px 14px',
            border: 'none',
            borderRadius: '4px',
            background: '#000',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '13px',
          }}
        >
          Apply
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        readOnly
        value={displayText}
        onClick={handleOpen}
        placeholder={label}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          fontFamily: 'inherit',
          fontSize: '14px',
          boxSizing: 'border-box',
        }}
      />
      {ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
};

export default MonthYearPicker;

